import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Checkbox,
  FormControlLabel,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormGroup,
  InputAdornment,
  Collapse,
  IconButton,
  Snackbar,
  Alert,
  Button,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  AcUnit as SnowIcon,
  Thermostat as TempIcon,
  Landscape as ConditionsIcon,
  CloudDownload as CloudDownloadIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';
import {
  WEATHER_CATEGORIES,
  PAVEMENT_CATEGORIES,
  SNOW_INTENSITIES
} from './winterLogOptions';
import WinterAdditionalServices from './WinterAdditionalServices';

/**
 * Winter Job Details Component
 * Displays winter-specific logging fields for snow removal jobs
 * Includes: Services Performed, Pavement Conditions, Weather Conditions, Salt Applications
 * hideAdditionalServices: the parent shows Site Check / Salt Bin Refill itself.
 */
const WinterJobDetails = ({ job, onUpdate, hideAdditionalServices = false }) => {
  const { t } = useTranslation();
  
  // Expanded sections state
  const [expandedSections, setExpandedSections] = useState({
    services: true,
    pavement: true,
    weather: true,
    salt: true
  });

  // Services state
  const [services, setServices] = useState({
    lot_plow: false,
    road_plow: false,
    city_walk_plow: false,
    internal_walkways_plow: false,
    unit_driver_steps_plow: false,
    lot_shovel: false,
    road_shovel: false,
    city_walk_shovel: false,
    internal_walkways_shovel: false,
    unit_driver_steps_shovel: false,
    additional_services: [],
    ...job.snow_services
  });

  // Pavement conditions state
  const [pavementConditions, setPavementConditions] = useState({
    is_snow: false,
    is_icy: false,
    is_clear: false,
    is_wet: false,
    is_slushy: false,
    depth_cm: '',
    condition_notes: '',
    observations: [],
    ...job.pavement_conditions
  });

  // Weather conditions state
  const [weatherConditions, setWeatherConditions] = useState({
    is_clear: false,
    is_snowing: false,
    is_freezing_rain: false,
    is_drifting: false,
    snow_intensity: '',
    temperature_celsius: '',
    wind_speed_kph: '',
    precipitation_amount_cm: '',
    condition_notes: '',
    observations: [],
    ...job.weather_conditions
  });

  // Salt applications state
  const [saltApplications, setSaltApplications] = useState({
    lot_salt_applied: false,
    lot_salt_amount: 0,
    road_salt_applied: false,
    road_salt_amount: 0,
    city_walk_salt_applied: false,
    city_walk_salt_amount: 0,
    internal_walkways_salt_applied: false,
    internal_walkways_salt_amount: 0,
    unit_driver_salt_applied: false,
    unit_driver_salt_amount: 0,
    // TKT-365: other materials, one total per visit.
    liquid_litres: 0,
    ice_melter_lbs: 0,
    grit_sand_lbs: 0,
    ...job.salt_application
  });

  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // TKT-258: weather auto-pull state. weatherFetching drives the spinner on
  // the precipitation "Pull" button; weatherAutoTried prevents the on-open
  // auto-fetch from firing more than once per mount.
  const [weatherFetching, setWeatherFetching] = useState(false);
  const [weatherAutoTried, setWeatherAutoTried] = useState(false);

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Auto-save with debounce, one timer per section so a quick change in
  // another section doesn't cancel this one's save.
  const saveTimers = React.useRef({});
  const debouncedSave = React.useCallback((section, saveFunction) => {
    clearTimeout(saveTimers.current[section]);
    saveTimers.current[section] = setTimeout(saveFunction, 1000);
  }, []);

  // Shared save helper. Surfaces backend errors to the user via the
  // error Snackbar — pre-fix, a 500 response silently did nothing and the
  // crew assumed their data had saved when it hadn't.
  const putJson = async (url, body, successKey) => {
    try {
      setSaving(true);
      const response = await makeApiCall(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const msg = errData.error || t('winterLogs.saveFailedGeneric');
        console.error(`Save failed (${url}):`, msg);
        setErrorMessage(msg);
        return false;
      }

      setSaveMessage(t(successKey));
      setTimeout(() => setSaveMessage(''), 2000);
      return true;
    } catch (error) {
      console.error(`Save network error (${url}):`, error);
      setErrorMessage(error.message || t('winterLogs.saveFailedGeneric'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  // When Crew Mode owns Site Check / Salt Bin Refill, leave them out so this
  // save doesn't overwrite what the crew ticked there (the server keeps it).
  const saveServices = (updatedServices) => {
    const body = { ...updatedServices };
    if (hideAdditionalServices) delete body.additional_services;
    return putJson(`api/jobs/${job.id}/snow-services`, body, 'winterLogs.servicesSaved');
  };

  const savePavementConditions = (updatedConditions) =>
    putJson(`api/jobs/${job.id}/pavement-conditions`, updatedConditions, 'winterLogs.pavementConditionsSaved');

  const saveWeatherConditions = (updatedConditions) =>
    putJson(`api/jobs/${job.id}/weather-conditions`, updatedConditions, 'winterLogs.weatherConditionsSaved');

  const saveSaltApplications = (updatedSalt) =>
    putJson(`api/jobs/${job.id}/salt-applications`, updatedSalt, 'winterLogs.saltApplicationsSaved');

  // TKT-258: pull real weather for this job's site/date from the backend
  // (Open-Meteo). `fields` controls which values get applied so the
  // precipitation "Pull" button can refresh just that one number while the
  // on-open auto-pull fills all three. Applied values are persisted so the
  // log keeps them without re-fetching next time.
  const pullWeather = async (fields = ['temperature_celsius', 'wind_speed_kph', 'precipitation_amount_cm']) => {
    try {
      setWeatherFetching(true);
      const response = await makeApiCall(`api/jobs/${job.id}/weather-fetch`);
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || t('winterLogs.weatherFetchFailed'));
      }
      const data = await response.json();
      const updated = { ...weatherConditions };
      let applied = false;
      for (const f of fields) {
        if (data[f] !== null && data[f] !== undefined) {
          updated[f] = data[f];
          applied = true;
        }
      }
      if (applied) {
        setWeatherConditions(updated);
        await saveWeatherConditions(updated);
      }
      return applied;
    } catch (error) {
      console.error('Weather pull failed:', error);
      setErrorMessage(error.message || t('winterLogs.weatherFetchFailed'));
      return false;
    } finally {
      setWeatherFetching(false);
    }
  };

  // On open, auto-populate temperature / wind / precipitation when the log
  // has none yet. Runs once per mount; staff can still edit precipitation
  // afterwards or hit the Pull button to refresh it.
  useEffect(() => {
    if (weatherAutoTried) return;
    const hasAny = weatherConditions.temperature_celsius
      || weatherConditions.wind_speed_kph
      || weatherConditions.precipitation_amount_cm;
    if (!hasAny) {
      setWeatherAutoTried(true);
      pullWeather();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id]);

  // Handle service checkbox change
  const handleServiceChange = (field) => (event) => {
    const updated = { ...services, [field]: event.target.checked };
    setServices(updated);
    debouncedSave('services', () => saveServices(updated));
  };

  // Handle pavement condition change
  const handlePavementChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    const updated = { ...pavementConditions, [field]: value };
    setPavementConditions(updated);
    debouncedSave('pavement', () => savePavementConditions(updated));
  };

  // Handle weather condition change
  const handleWeatherChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    const updated = { ...weatherConditions, [field]: value };
    setWeatherConditions(updated);
    debouncedSave('weather', () => saveWeatherConditions(updated));
  };

  // Site Check / Salt Bin Refill checkboxes
  const handleAdditionalServicesChange = (codes) => {
    const updated = { ...services, additional_services: codes };
    setServices(updated);
    debouncedSave('services', () => saveServices(updated));
  };

  // Snowfall intensity: tapping the selected level again clears it.
  const handleSnowIntensityChange = (_event, value) => {
    const updated = { ...weatherConditions, snow_intensity: value || '' };
    setWeatherConditions(updated);
    debouncedSave('weather', () => saveWeatherConditions(updated));
  };

  // Observation checkboxes: original options are their own columns, new
  // ones are codes in the `observations` list.
  const isObserved = (conditions, option) => (option.field
    ? !!conditions[option.field]
    : (conditions.observations || []).includes(option.code));

  const withObservation = (conditions, option, checked) => {
    if (option.field) return { ...conditions, [option.field]: checked };
    const rest = (conditions.observations || []).filter((code) => code !== option.code);
    return { ...conditions, observations: checked ? [...rest, option.code] : rest };
  };

  const handleObservationChange = (section, option) => (event) => {
    const { checked } = event.target;
    if (section === 'weather') {
      const updated = withObservation(weatherConditions, option, checked);
      setWeatherConditions(updated);
      debouncedSave('weather', () => saveWeatherConditions(updated));
    } else {
      const updated = withObservation(pavementConditions, option, checked);
      setPavementConditions(updated);
      debouncedSave('pavement', () => savePavementConditions(updated));
    }
  };

  const renderObservationGroups = (section, categories, conditions) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('winterLogs.observationsOptional')}
      </Typography>
      {categories.map((category) => (
        <Box key={category.key} sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2">{t(`winterLogs.categories.${category.key}`)}</Typography>
          <FormGroup row>
            {category.options.map((option) => (
              <FormControlLabel
                key={option.field || option.code}
                control={
                  <Checkbox
                    checked={isObserved(conditions, option)}
                    onChange={handleObservationChange(section, option)}
                    size="large"
                  />
                }
                label={t(`winterLogs.${option.label}`)}
              />
            ))}
          </FormGroup>
          {category.showSnowIntensity && (
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
              <Typography variant="body2">{t('winterLogs.snowfall')}:</Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={weatherConditions.snow_intensity || null}
                onChange={handleSnowIntensityChange}
                aria-label={t('winterLogs.snowfall')}
              >
                {SNOW_INTENSITIES.map((level) => (
                  <ToggleButton key={level} value={level} sx={{ px: 2 }}>
                    {t(`winterLogs.intensity.${level}`)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );

  // Handle salt application change
  const handleSaltChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    const updated = { ...saltApplications, [field]: value };
    setSaltApplications(updated);
    debouncedSave('salt', () => saveSaltApplications(updated));
  };

  const renderSaltControl = (checkedField, amountField, ariaLabel) => (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      gap={1}
      sx={{ minWidth: 150 }}
    >
      <Checkbox
        checked={saltApplications[checkedField]}
        onChange={handleSaltChange(checkedField)}
        size="large"
        inputProps={{ 'aria-label': ariaLabel }}
      />
      <TextField
        value={saltApplications[amountField]}
        onChange={handleSaltChange(amountField)}
        type="number"
        size="small"
        sx={{ width: 96 }}
        InputProps={{
          /* TKT-258: Naomi - salt is tracked in lbs, not bags. The stored
             number now represents lbs directly (no conversion). */
          endAdornment: <InputAdornment position="end">{t('winterLogs.lbs')}</InputAdornment>
        }}
        inputProps={{ min: 0, 'aria-label': `${ariaLabel} ${t('winterLogs.lbs')}` }}
        disabled={!saltApplications[checkedField]}
      />
    </Box>
  );

  // TKT-365: liquid, ice melter and grit/sand used on this visit.
  const renderMaterialField = (field, label, unit) => (
    <Grid size={{ xs: 12, sm: 4 }}>
      <TextField
        fullWidth
        label={label}
        value={saltApplications[field] ?? 0}
        onChange={handleSaltChange(field)}
        type="number"
        size="small"
        InputProps={{
          endAdornment: <InputAdornment position="end">{unit}</InputAdornment>
        }}
        inputProps={{ min: 0, 'aria-label': `${label} ${unit}` }}
      />
    </Grid>
  );

  return (
    <Box sx={{ mt: 3 }}>
      {/* Save success indicator - Snackbar at bottom */}
      <Snackbar
        open={!!saveMessage}
        message={saveMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={2000}
      />

      {/* Save error indicator - sticky until dismissed */}
      <Snackbar
        open={!!errorMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setErrorMessage('')}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setErrorMessage('')}
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Services Performed */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <SnowIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">{t('winterLogs.servicesPerformed')}</Typography>
            </Box>
            <IconButton onClick={() => toggleSection('services')} size="small">
              <ExpandIcon 
                sx={{ 
                  transform: expandedSections.services ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s'
                }} 
              />
            </IconButton>
          </Box>
          
          <Collapse in={expandedSections.services}>
            {/* TKT-486: Site Check and Salt Bin Refill are their own check-off
                items. Crew Mode shows them in its Clock In / Clock Out box instead. */}
            {!hideAdditionalServices && (
              <Box sx={{ mt: 2 }}>
                <WinterAdditionalServices
                  value={services.additional_services}
                  onChange={handleAdditionalServicesChange}
                />
              </Box>
            )}
            <TableContainer component={Paper} elevation={0} sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>{t('winterLogs.area')}</strong></TableCell>
                    <TableCell align="center"><strong>{t('winterLogs.plow')}</strong></TableCell>
                    <TableCell align="center"><strong>{t('winterLogs.shovel')}</strong></TableCell>
                    <TableCell align="center"><strong>{t('winterLogs.salt')}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Lot */}
                  <TableRow>
                    <TableCell>{t('winterLogs.lot')}</TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={services.lot_plow}
                        onChange={handleServiceChange('lot_plow')}
                        size="large"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={services.lot_shovel}
                        onChange={handleServiceChange('lot_shovel')}
                        size="large"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {renderSaltControl('lot_salt_applied', 'lot_salt_amount', `${t('winterLogs.lot')} ${t('winterLogs.salt')}`)}
                    </TableCell>
                  </TableRow>

                  {/* Road */}
                  <TableRow>
                    <TableCell>{t('winterLogs.road')}</TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={services.road_plow}
                        onChange={handleServiceChange('road_plow')}
                        size="large"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={services.road_shovel}
                        onChange={handleServiceChange('road_shovel')}
                        size="large"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {renderSaltControl('road_salt_applied', 'road_salt_amount', `${t('winterLogs.road')} ${t('winterLogs.salt')}`)}
                    </TableCell>
                  </TableRow>

                  {/* City Walk */}
                  <TableRow>
                    <TableCell>{t('winterLogs.cityWalk')}</TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={services.city_walk_plow}
                        onChange={handleServiceChange('city_walk_plow')}
                        size="large"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={services.city_walk_shovel}
                        onChange={handleServiceChange('city_walk_shovel')}
                        size="large"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {renderSaltControl('city_walk_salt_applied', 'city_walk_salt_amount', `${t('winterLogs.cityWalk')} ${t('winterLogs.salt')}`)}
                    </TableCell>
                  </TableRow>

                  {/* Internal Walkways */}
                  <TableRow>
                    <TableCell>{t('winterLogs.internalWalkways')}</TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={services.internal_walkways_plow}
                        onChange={handleServiceChange('internal_walkways_plow')}
                        size="large"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={services.internal_walkways_shovel}
                        onChange={handleServiceChange('internal_walkways_shovel')}
                        size="large"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {renderSaltControl('internal_walkways_salt_applied', 'internal_walkways_salt_amount', `${t('winterLogs.internalWalkways')} ${t('winterLogs.salt')}`)}
                    </TableCell>
                  </TableRow>

                  {/* Unit Driver/Steps */}
                  <TableRow>
                    <TableCell>{t('winterLogs.unitDriverSteps')}</TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={services.unit_driver_steps_plow}
                        onChange={handleServiceChange('unit_driver_steps_plow')}
                        size="large"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={services.unit_driver_steps_shovel}
                        onChange={handleServiceChange('unit_driver_steps_shovel')}
                        size="large"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {renderSaltControl('unit_driver_salt_applied', 'unit_driver_salt_amount', `${t('winterLogs.unitDriverSteps')} ${t('winterLogs.salt')}`)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
              {t('winterLogs.otherMaterials')}
            </Typography>
            <Grid container spacing={2}>
              {renderMaterialField('liquid_litres', t('winterLogs.liquid'), t('winterLogs.litres'))}
              {renderMaterialField('ice_melter_lbs', t('winterLogs.iceMelter'), t('winterLogs.lbs'))}
              {renderMaterialField('grit_sand_lbs', t('winterLogs.gritSand'), t('winterLogs.lbs'))}
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {/* Pavement Conditions */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <ConditionsIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">{t('winterLogs.pavementConditions')}</Typography>
            </Box>
            <IconButton onClick={() => toggleSection('pavement')} size="small">
              <ExpandIcon 
                sx={{ 
                  transform: expandedSections.pavement ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s'
                }} 
              />
            </IconButton>
          </Box>
          
          <Collapse in={expandedSections.pavement}>
            <Box sx={{ mt: 2 }}>
              {renderObservationGroups('pavement', PAVEMENT_CATEGORIES, pavementConditions)}

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label={`${t('winterLogs.depth')} (${t('winterLogs.cm')})`}
                    value={pavementConditions.depth_cm}
                    onChange={handlePavementChange('depth_cm')}
                    type="number"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">{t('winterLogs.cm')}</InputAdornment>
                    }}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label={t('winterLogs.notes')}
                    value={pavementConditions.condition_notes}
                    onChange={handlePavementChange('condition_notes')}
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* Weather Conditions */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <TempIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">{t('winterLogs.weatherConditions')}</Typography>
            </Box>
            <IconButton onClick={() => toggleSection('weather')} size="small">
              <ExpandIcon 
                sx={{ 
                  transform: expandedSections.weather ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s'
                }} 
              />
            </IconButton>
          </Box>
          
          <Collapse in={expandedSections.weather}>
            <Box sx={{ mt: 2 }}>
              {renderObservationGroups('weather', WEATHER_CATEGORIES, weatherConditions)}

              {/* TKT-258: Temperature + Wind Speed auto-populate from the
                  weather feed (Open-Meteo) when the log is opened and stay
                  read-only. Precipitation Amount is staff-editable again
                  (Naomi reversed TKT-208 for this field specifically) with a
                  "Pull from weather data" button that fills it on demand. */}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label={t('winterLogs.temperature')}
                    value={weatherConditions.temperature_celsius ?? ''}
                    type="number"
                    disabled
                    helperText={t('winterLogs.weatherProvidedField', 'Provided by weather data; crew entry is not required')}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">°C</InputAdornment>
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label={t('winterLogs.windSpeed')}
                    value={weatherConditions.wind_speed_kph ?? ''}
                    type="number"
                    disabled
                    helperText={t('winterLogs.weatherProvidedField', 'Provided by weather data; crew entry is not required')}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">km/h</InputAdornment>
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box display="flex" alignItems="flex-start" gap={1}>
                    <TextField
                      fullWidth
                      label={`${t('winterLogs.precipitationAmount')} (${t('winterLogs.cm')})`}
                      value={weatherConditions.precipitation_amount_cm ?? ''}
                      onChange={handleWeatherChange('precipitation_amount_cm')}
                      type="number"
                      helperText={t('winterLogs.precipitationEditableHint', 'Enter the amount, or pull it from weather data')}
                      inputProps={{ min: 0, step: 0.1 }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">{t('winterLogs.cm')}</InputAdornment>
                      }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => pullWeather(['precipitation_amount_cm'])}
                      disabled={weatherFetching}
                      startIcon={weatherFetching ? <CircularProgress size={16} /> : <CloudDownloadIcon />}
                      sx={{ mt: 1, whiteSpace: 'nowrap', flexShrink: 0 }}
                      aria-label={t('winterLogs.pullFromWeather', 'Pull from weather data')}
                    >
                      {t('winterLogs.pull', 'Pull')}
                    </Button>
                  </Box>
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label={t('winterLogs.notes')}
                    value={weatherConditions.condition_notes}
                    onChange={handleWeatherChange('condition_notes')}
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    </Box>
  );
};

export default WinterJobDetails;

